import { createSelector } from '@reduxjs/toolkit';
import {
  fetchCustomerRecipientsDomainRequest,
  fetchCustomerUsersDomainRequest,
  fetchUserProfileAggsRequest,
  fetchUserRequest,
  fetchUsersCountRequest,
  fetchUsersRequest,
  fetchUsersSeatsRequest,
  saveUserRequest,
} from 'actions';
import { GlobalState } from 'reducers';
import type {
  Customer,
  EntityMetadata,
  NormalizedResource,
  Role,
  Selector,
  UUID,
  User,
  UserWithAggs,
} from 'types';
import { getPreferOriginalLanguages, getUserMetadataData } from 'utils/userMetadata';

export const getUsersLoading: Selector<boolean> = (state) =>
  state.users.loading.includes(fetchUsersRequest.toString());

export const getSelectedUser: Selector<User | null> = (state) => state.users.selectedUser;

export const getSingleUser: Selector<User, [string]> = (state, userId) => state.users.items[userId];

export const getUser =
  (userId: UUID | undefined): Selector<UserWithAggs | null> =>
  ({ users }): UserWithAggs | null =>
    userId ? users.items[userId] : null;

export const getUserLoading: Selector<boolean> = (state) =>
  state.users.loading.includes(saveUserRequest.toString()) ||
  state.users.loading.includes(fetchUserRequest.toString());

export const getUsers: Selector<NormalizedResource<User>> = (state) => state.users.items;

export const getUsersForFilterPills: Selector<User[]> = (state) => {
  const { filterItems } = state.users;

  return Object.keys(filterItems).map((uuid) => filterItems[uuid]);
};

export const getUsersTotalCount: Selector<number> = (state) => state.users.count;

export const getUsersTotalCountLoading: Selector<boolean> = (state) =>
  state.users.loading.includes(fetchUsersCountRequest.toString());

export const getAvailablesRoles: Selector<string[]> = (state) => state.users.availableRoles;

export const getUsersList: Selector<User[]> = createSelector(
  [(state: GlobalState): GlobalState['users']['items'] => state.users.items],
  (users) => Object.keys(users).map((uuid) => users[uuid])
);

export const getUsersNoSuperAdminList: Selector<User[]> = createSelector(
  [(state: GlobalState): GlobalState['users']['items'] => state.users.items],
  (users) =>
    Object.keys(users)
      .map((uuid) => users[uuid])
      .filter((user) => !user.roles?.includes('super-admin'))
);

export const getReviewersWithCounts: Selector<(User & { count: number })[]> = createSelector(
  [
    (state: GlobalState): GlobalState['envelopes']['filtersCounts'] =>
      state.envelopes.filtersCounts,
    (state: GlobalState): GlobalState['users']['items'] => state.users.items,
  ],
  (counts, users) => {
    const usersCounts = counts.reviewers || [];

    return Object.values(users)
      .filter((user) => !user.roles?.includes('super-admin'))
      .map((u) => {
        const count = usersCounts?.find((c) => c.uuid === u.uuid)?.count || 0;
        return { ...u, count };
      });
  }
);

export const getUsersFilteredByRoles = (state: GlobalState, roles: Role[]): User[] => {
  const users = getUsersList(state);
  if (roles) {
    return users.filter((user) => roles.some((r) => user.roles && user.roles.includes(r)));
  }

  return users;
};

export const getCurrentUserMetadata: Selector<EntityMetadata | null> = (state) => {
  const metadata = state.users.currentUserMetadata;
  if (!metadata) return null;
  if (typeof metadata === 'object' && !Object.keys(metadata).length) return null;
  return metadata;
};

export const getUserShowHighlights: Selector<EntityMetadata | null> = (state) =>
  getCurrentUserMetadata(state);

export const getUserPreferOriginalLanguages: Selector<string[]> = createSelector(
  [getCurrentUserMetadata],
  (metadata) => getPreferOriginalLanguages(getUserMetadataData(metadata))
);

export const getCustomerSenderDomain: Selector<{ uuid: string; name: string; count: number }[]> =
  createSelector(
    [(state: GlobalState): GlobalState['users']['senderDomains'] => state.users.senderDomains],
    (senderDomains) =>
      senderDomains
        .filter((domain) => domain.domain.length > 0)
        .map((domain) => ({
          uuid: domain.domain,
          name: domain.domain,
          count: domain.count,
        }))
  );

export const getCustomerSenderDomainLoading: Selector<boolean> = (state) =>
  state.users.loading.includes(fetchCustomerUsersDomainRequest.toString());

export const getCustomerSenderDomainCount: Selector<number> = (state) =>
  state.users.senderDomainsCount;

export const getCustomerRecipientsDomain: Selector<
  { uuid: string; name: string; count: number }[]
> = createSelector(
  [
    (state: GlobalState): GlobalState['users']['recipientsDomains'] =>
      state.users.recipientsDomains,
  ],
  (recipientsDomains) =>
    recipientsDomains
      .filter((domain) => domain.domain.length > 0)
      .map((domain) => ({
        uuid: domain.domain,
        name: domain.domain,
        count: domain.count,
      }))
);

export const getCustomerRecipientsDomainLoading: Selector<boolean> = (state) =>
  state.users.loading.includes(fetchCustomerRecipientsDomainRequest.toString());

export const getCustomerRecipientsDomainCount: Selector<number> = (state) =>
  state.users.recipientsDomainsCount;

export const getUserRolesCount: Selector<{ [key: string]: number }> = (state) => {
  const { rolesCount } = state.users;
  return rolesCount;
};

export const getUserIntegrationsCount: Selector<{ [key: string]: number }> = (state) => {
  const { integrationsCount } = state.users;
  return integrationsCount;
};

export const getTeamUsersCount: Selector<{ [key: string]: number }> = (state) => {
  const { teamUsersCount } = state.users;
  return teamUsersCount;
};

export const getMetricCustomers: Selector<Customer[]> = (state) => state.users.metricCustomers;

export const getFetchUserProfileAggsLoading: Selector<boolean> = (state) =>
  state.users.loading.includes(fetchUserProfileAggsRequest.toString());

export const getFetchUsersSeatsLoading: Selector<boolean> = (state) =>
  state.users.loading.includes(fetchUsersSeatsRequest.toString());

export const getUsersSeats: Selector<{
  insight: number;
  prevent: number;
  insight_total: number;
  prevent_total: number;
}> = (state) => state.users.userSeats;
